import React, { createContext, useContext } from "react"
import { useRouteMatch } from "react-router-dom"
import { useFeatureLayer } from "../feature-layer/useFeatureLayer"
import { useFeatureLayerFeature } from "../feature-layer/useFeatureLayerFeature"

/** Generate the context */
export type PromiseStatus = "pending" | "resolved" | "rejected"
const DataContext = createContext<
  | {
      activeFeatureLayer: [
        __esri.FeatureLayer | undefined,
        Error | undefined,
        PromiseStatus
      ]
      activeFeature: [
        __esri.Graphic | undefined,
        Error | undefined,
        PromiseStatus
      ]
    }
  | undefined
>(undefined)

/** Listens for changes in the URL and grabs data when it needs to */
export const DataProvider: React.FC = ({ children }) => {
  /** Gets params in order to get data */
  const match = useRouteMatch<{ featureLayerId: string; featureId: string }>(
    "/layers/:featureLayerId?/:featureId?"
  )

  /** Get the feature layer param */
  const { featureLayerId, featureId } = match
    ? match.params
    : { featureLayerId: undefined, featureId: undefined }

  /** Get active feature layer based on featureLayerId (returns data, error and status) */
  const activeFeatureLayer = useFeatureLayer(featureLayerId)

  /** Get active feature based on featureId (returns data, error and status) */
  const activeFeature = useFeatureLayerFeature(
    activeFeatureLayer[0],
    featureId !== "settings" ? featureId : undefined
  )

  return (
    <DataContext.Provider value={{ activeFeatureLayer, activeFeature }}>
      {children}
    </DataContext.Provider>
  )
}

/** Hook to consume the context */
export const useData = () => {
  const context = useContext(DataContext)
  if (!context) throw Error("Not inside <DataProvider />")
  return context
}
